import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import treatmentService from '../../services/TreatmentService';
import practitionerService from '../../services/PractitionerService';
import venueService from '../../services/VenueService';
import customerService from '../../services/CustomerService';
import appointmentService from '../../services/AppointmentService';
import { Input, Select, Button, CustomDropdown } from '../ui';
import CustomerInfoCard from '../customers/CustomerInfoCard';

/**
 * AppointmentForm component for creating and editing appointments
 */
const AppointmentForm = ({
  appointment,
  onSubmit,
  onCancel,
  isLoading = false,
  customers = [],
  preSelectedCustomerId = null,
}) => {
  // State for form data
  const [formData, setFormData] = useState({
    customerId: '',
    treatmentId: '',
    practitionerId: '',
    venueId: '',
    appointmentDate: '',
    appointmentTime: '',
    duration: 60, // default duration
    status: 'scheduled',
    notes: ''
  });

  // Options for dropdowns
  const [treatments, setTreatments] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [venues, setVenues] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerAppointments, setCustomerAppointments] = useState([]);
  const [recommendedPractitioner, setRecommendedPractitioner] = useState(null);
  const [recommendedTreatment, setRecommendedTreatment] = useState(null);

  // Helper to generate half-hour time slots
  const generateTimeSlots = (start = 8, end = 21) => {
    const slots = [];
    for (let h = start; h < end; h++) {
      slots.push(`${h.toString().padStart(2, '0')}:00`);
      slots.push(`${h.toString().padStart(2, '0')}:30`);
    }
    slots.push(`${end.toString().padStart(2, '0')}:00`);
    return slots;
  };
  const timeSlots = generateTimeSlots();

  // Load reference data on component mount
  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        // Load treatments
        const treatmentsData = treatmentService.getAllTreatments()
          .filter(treatment => treatment.isActive);
        setTreatments(treatmentsData);

        // Load active practitioners
        const practitionersData = practitionerService.getAllPractitioners()
          .filter(practitioner => practitioner.isActive);
        setPractitioners(practitionersData);

        // Load available venues
        const venuesData = venueService.getAllVenues()
          .filter(venue => venue.isActive);
        setVenues(venuesData);

        // Set initial filtered customers
        setFilteredCustomers(customers.slice(0, 5));

      } catch (err) {
        console.error('Error loading reference data:', err);
      }
    };

    loadReferenceData();
  }, [customers]);

  // If editing, populate form with appointment data
  useEffect(() => {
    if (appointment) {
      // Use startTime if appointmentTime is not available (for compatibility)
      const timeToDisplay = appointment.appointmentTime || appointment.startTime;

      // Calculate duration from startTime and endTime if not provided
      let calculatedDuration = appointment.duration || 60;
      if (!appointment.duration && appointment.startTime && appointment.endTime) {
        const start = new Date(appointment.startTime);
        const end = new Date(appointment.endTime);
        calculatedDuration = Math.round((end - start) / 60000); // Convert ms to minutes
      }

      // 解析日期和时间
      let appointmentDate = '';
      let appointmentTime = '';
      if (timeToDisplay) {
        const d = new Date(timeToDisplay);
        appointmentDate = d.toISOString().slice(0, 10); // yyyy-MM-dd
        const hours = d.getHours().toString().padStart(2, '0');
        const minutes = d.getMinutes().toString().padStart(2, '0');
        appointmentTime = `${hours}:${minutes}`;
      }

      setFormData({
        customerId: appointment.customerId || '',
        treatmentId: appointment.treatmentId || '',
        practitionerId: appointment.practitionerId || '',
        venueId: appointment.venueId || '',
        appointmentDate,
        appointmentTime,
        duration: calculatedDuration,
        status: appointment.status || 'scheduled',
        notes: appointment.notes || ''
      });
    } else if (preSelectedCustomerId) {
      setFormData(prevState => ({
        ...prevState,
        customerId: preSelectedCustomerId
      }));
    }
  }, [appointment, preSelectedCustomerId]);

  // Update selected customer and fetch their appointments
  useEffect(() => {
    const customerId = formData.customerId;
    if (customerId) {
      const customer = customers.find(c => c.id === customerId);
      setSelectedCustomer(customer);
      // Fetch appointment history
      const history = appointmentService.getAppointmentsByCustomer(customerId);
      setCustomerAppointments(history);
      // Recommend practitioner and treatment
      if (history.length > 0) {
        // Recommend the most frequent practitioner
        const practitionerCount = {};
        const treatmentCount = {};
        history.forEach(a => {
          if (a.practitionerId) {
            practitionerCount[a.practitionerId] = (practitionerCount[a.practitionerId] || 0) + 1;
          }
          if (a.treatmentId) {
            treatmentCount[a.treatmentId] = (treatmentCount[a.treatmentId] || 0) + 1;
          }
        });
        const topPractitionerId = Object.entries(practitionerCount).sort((a, b) => b[1] - a[1])[0]?.[0];
        const topTreatmentId = Object.entries(treatmentCount).sort((a, b) => b[1] - a[1])[0]?.[0];
        setRecommendedPractitioner(practitioners.find(p => p.id === topPractitionerId) || null);
        setRecommendedTreatment(treatments.find(t => t.id === topTreatmentId) || null);
      } else {
        setRecommendedPractitioner(null);
        setRecommendedTreatment(null);
      }
    } else {
      setSelectedCustomer(null);
      setCustomerAppointments([]);
      setRecommendedPractitioner(null);
      setRecommendedTreatment(null);
    }
  }, [formData.customerId, customers, practitioners, treatments]);

  // Format datetime string for datetime-local input
  const formatDateTime = (isoString) => {
    if (!isoString) return '';

    // Get the local date-time without the 'Z' suffix
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // If treatment changes, update duration based on selected treatment
    if (name === 'treatmentId') {
      const selectedTreatment = treatments.find(t => t.id === value);
      if (selectedTreatment && selectedTreatment.duration) {
        setFormData(prev => ({
          ...prev,
          duration: selectedTreatment.duration
        }));
      }
    }
  };

  // Handle customer search
  const handleCustomerSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);

    if (term.length > 0) {
      const filtered = customers.filter(customer =>
        customer.name?.toLowerCase().includes(term.toLowerCase()) ||
        customer.phoneNumber?.includes(term)
      );
      setFilteredCustomers(filtered.slice(0, 5));
      setShowCustomerDropdown(true);
    } else {
      setFilteredCustomers(customers.slice(0, 5));
      setShowCustomerDropdown(false);
    }
  };

  // Handle customer selection
  const handleCustomerSelect = (customer) => {
    setFormData(prev => ({
      ...prev,
      customerId: customer.id
    }));
    setSearchTerm(customer.name);
    setShowCustomerDropdown(false);
    setSelectedCustomer(customer);
  };

  // One-click fill for recommended practitioner/treatment
  const handleFillRecommendedPractitioner = () => {
    if (recommendedPractitioner) {
      setFormData(prev => ({ ...prev, practitionerId: recommendedPractitioner.id }));
    }
  };
  const handleFillRecommendedTreatment = () => {
    if (recommendedTreatment) {
      setFormData(prev => ({ ...prev, treatmentId: recommendedTreatment.id }));
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    let appointmentData = { ...formData, status: 'scheduled' };
    if (formData.appointmentDate && formData.appointmentTime) {
      // Combine date and time to ISO string
      const dateStr = formData.appointmentDate;
      const timeStr = formData.appointmentTime;
      const iso = new Date(`${dateStr}T${timeStr}`).toISOString();
      appointmentData.appointmentTime = iso;
    }
    onSubmit(appointmentData);
  };

  // Find customer name by ID for display
  const getCustomerNameById = (id) => {
    const customer = customers.find(c => c.id === id);
    return customer ? customer.name : '';
  };

  // If customer is preselected, show their name in search field
  useEffect(() => {
    if (preSelectedCustomerId || formData.customerId) {
      const customerId = preSelectedCustomerId || formData.customerId;
      const customerName = getCustomerNameById(customerId);
      if (customerName) {
        setSearchTerm(customerName);
      }
    }
  }, [preSelectedCustomerId, formData.customerId, customers]);

  // 分组：特色项目和基础项目（根据type字段）
  const featuredTreatments = treatments.filter(t => t.type === 'project');
  const basicTreatments = treatments.filter(t => t.type === 'basic');

  return (
    <form onSubmit={handleSubmit} className="flex flex-col md:flex-row gap-8">
      {/* Left: Customer Info Card */}
      <div className="w-full md:w-1/2">
        <CustomerInfoCard
          customer={selectedCustomer}
          appointments={customerAppointments}
          recommendedPractitioner={recommendedPractitioner}
          recommendedTreatment={recommendedTreatment}
          onFillPractitioner={handleFillRecommendedPractitioner}
          onFillTreatment={handleFillRecommendedTreatment}
          searchTerm={searchTerm}
          onCustomerSearch={handleCustomerSearch}
          filteredCustomers={filteredCustomers}
          showCustomerDropdown={showCustomerDropdown}
          onCustomerSelect={handleCustomerSelect}
          formData={formData}
        />
      </div>
      {/* Right: Appointment Form Fields */}
      <div className="w-full md:w-1/2 space-y-6">
        {/* Treatment Selection */}
        <label className="block text-sm font-medium text-gray-700 mb-2">调理项目</label>
        <select
          value={formData.treatmentId}
          onChange={handleInputChange}
          name="treatmentId"
          required
          className="w-full px-4 py-3 text-sm border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
        >
          <option value="">请选择项目</option>
          {featuredTreatments.length > 0 && (
            <optgroup label="特色项目">
              {featuredTreatments.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.duration}分钟)
                </option>
              ))}
            </optgroup>
          )}
          {basicTreatments.length > 0 && (
            <optgroup label="基础项目">
              {basicTreatments.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.duration}分钟)
                </option>
              ))}
            </optgroup>
          )}
        </select>

        {/* Practitioner Selection */}
        <Select
          label="调理师"
          value={formData.practitionerId}
          onChange={handleInputChange}
          name="practitionerId"
          required
          options={[
            { value: '', label: '请选择调理师' },
            ...practitioners.map((practitioner) => ({
              value: practitioner.id,
              label: practitioner.name
            }))
          ]}
        />

        {/* 日期和时间同一行 */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="预约日期"
            type="date"
            name="appointmentDate"
            value={formData.appointmentDate}
            onChange={handleInputChange}
            required
          />
          <Select
            label="预约时间"
            name="appointmentTime"
            value={formData.appointmentTime}
            onChange={handleInputChange}
            required
            options={[
              { value: '', label: '请选择时间' },
              ...timeSlots.map(t => ({ value: t, label: t }))
            ]}
          />
        </div>

        {/* 场地和时长同一行 */}
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="场地"
            value={formData.venueId}
            onChange={handleInputChange}
            name="venueId"
            options={[
              { value: '', label: '请选择场地' },
              ...venues.map((venue) => ({
                value: venue.id,
                label: `${venue.name} (${venue.location || '未指定位置'})`
              }))
            ]}
          />
          <Input
            label="时长 (分钟)"
            type="number"
            name="duration"
            min="15"
            max="240"
            step="5"
            value={formData.duration}
            onChange={handleInputChange}
            placeholder="请输入调理时长"
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            备注信息
          </label>
          <textarea
            name="notes"
            rows={4}
            value={formData.notes}
            onChange={handleInputChange}
            className="w-full px-4 py-3 text-sm border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
            placeholder="添加任何特殊要求或注意事项，如客户偏好、过敏信息等"
          />
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-100">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            取消
          </Button>
          <Button
            type="submit"
            disabled={isLoading}
            loading={isLoading}
          >
            {appointment ? '保存修改' : '创建预约'}
          </Button>
        </div>
      </div>
    </form>
  );
};

AppointmentForm.propTypes = {
  appointment: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  customers: PropTypes.array,
  preSelectedCustomerId: PropTypes.string,
};

export default AppointmentForm;